// Chemin: C:\gestion-copro\src\pages\admin\SignalementsPage.tsx
// Session 17 : Ajout infobulle d'aide
// Session 212 : Filtrage par coproprieteId (étanchéité inter-copropriétés)

import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCopropriete } from '../../context/CoproprieteContext';
import { getSignalements } from '../../services/signalement.service';
import { Signalement, SignalementStatut } from '../../types/signalement.types';
import { updateStatutSignalement } from '../../services/signalement.service';
import AdminLayout from '../../components/layout/AdminLayout';
import HelpBanner from '../../components/ui/HelpBanner';

// ============================================
// TYPES ET CONSTANTES
// ============================================

const STATUTS: { value: SignalementStatut; label: string; color: string }[] = [
  { value: 'nouveau', label: 'Nouveau', color: 'bg-red-100 text-red-700' },
  { value: 'en_cours', label: 'En cours', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'transmis_syndic', label: 'Transmis syndic', color: 'bg-blue-100 text-blue-700' },
  { value: 'transmis_promoteur', label: 'Transmis promoteur', color: 'bg-purple-100 text-purple-700' },
  { value: 'resolu', label: 'Résolu', color: 'bg-green-100 text-green-700' },
  { value: 'refuse', label: 'Refusé', color: 'bg-gray-100 text-gray-700' },
];

const URGENCES = [
  { value: 'basse', label: 'Basse', color: 'text-green-500' },
  { value: 'moyenne', label: 'Moyenne', color: 'text-yellow-500' },
  { value: 'haute', label: 'Haute', color: 'text-orange-500' },
  { value: 'critique', label: 'Critique', color: 'text-red-500' },
];

const LOCALISATIONS = [
  { value: 'parties_communes', label: 'Parties communes' },
  { value: 'parties_privatives', label: 'Parties privatives' },
  { value: 'equipements', label: 'Équipements' },
];

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const SignalementsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { coproprieteId } = useCopropriete();

  const [signalements, setSignalements] = useState<Signalement[]>([]);
  const [filteredSignalements, setFilteredSignalements] = useState<Signalement[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  // Filtres
  const [filtreStatut, setFiltreStatut] = useState(searchParams.get('statut') || '');
  const [filtreUrgence, setFiltreUrgence] = useState('');
  const [filtreLocalisation, setFiltreLocalisation] = useState('');
  const [recherche, setRecherche] = useState('');

  // Charger les signalements filtrés par copropriété
  useEffect(() => {
    if (!coproprieteId) return;

    const loadSignalements = async () => {
      try {
        setLoading(true);
        const data = await getSignalements(undefined, coproprieteId);
        setSignalements(data);
      } catch (error) {
        console.error('Erreur chargement signalements:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSignalements();
  }, [coproprieteId]);

  // Appliquer les filtres
  useEffect(() => {
    let filtered = [...signalements];

    if (filtreStatut) {
      filtered = filtered.filter((s) => s.statut === filtreStatut);
    }
    if (filtreUrgence) {
      filtered = filtered.filter((s) => s.urgence === filtreUrgence);
    }
    if (filtreLocalisation) {
      filtered = filtered.filter((s) => s.localisation === filtreLocalisation);
    }
    if (recherche) {
      const search = recherche.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.titre.toLowerCase().includes(search) ||
          s.description.toLowerCase().includes(search) ||
          s.localisationDetail.toLowerCase().includes(search) ||
          s.declarant.nom.toLowerCase().includes(search)
      );
    }

    setFilteredSignalements(filtered);
  }, [signalements, filtreStatut, filtreUrgence, filtreLocalisation, recherche]);

  // Changer le statut d'un signalement
  const changerStatut = async (id: string, nouveauStatut: SignalementStatut) => {
    setUpdating(id);
    try {
      await updateStatutSignalement(id, nouveauStatut, 'Gestionnaire');
      setSignalements((prev) =>
        prev.map((s) => s.id === id ? { ...s, statut: nouveauStatut } : s)
      );
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
      alert('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const resetFiltres = () => {
    setFiltreStatut('');
    setFiltreUrgence('');
    setFiltreLocalisation('');
    setRecherche('');
    setSearchParams({});
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Titre */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Signalements</h1>
          <p className="text-gray-500">{filteredSignalements.length} signalement(s)</p>
        </div>
        <a
          href="/signaler"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors"
        >
          <span>+</span>
          <span>Nouveau signalement</span>
        </a>
      </div>

      {/* Aide contextuelle */}
      <div className="mb-6">
        <HelpBanner
          storageKey="help_dismissed_signalements"
          message="Un signalement peut être rattaché à une affaire pour un meilleur suivi. Pour cela, ouvrez l'affaire concernée (menu Affaires) puis utilisez le bouton 'Rattacher un signalement'."
        />
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <input
              type="text"
              placeholder="Rechercher..."
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <select
            value={filtreStatut}
            onChange={(e) => setFiltreStatut(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tous les statuts</option>
            {STATUTS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          <select
            value={filtreUrgence}
            onChange={(e) => setFiltreUrgence(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Toutes urgences</option>
            {URGENCES.map((u) => (
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>

          <select
            value={filtreLocalisation}
            onChange={(e) => setFiltreLocalisation(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Toutes localisations</option>
            {LOCALISATIONS.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>

        {(filtreStatut || filtreUrgence || filtreLocalisation || recherche) && (
          <div className="mt-4 flex justify-end">
            <button onClick={resetFiltres} className="text-sm text-gray-500 hover:text-gray-700">
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </div>

      {/* Liste des signalements */}
      {filteredSignalements.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <p className="text-gray-500">Aucun signalement trouvé</p>
          {(filtreStatut || filtreUrgence || filtreLocalisation || recherche) && (
            <button onClick={resetFiltres} className="mt-4 text-blue-600 hover:text-blue-800">
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Version desktop : tableau */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-gray-600">Date</th>
                  <th className="text-left p-4 font-semibold text-gray-600">Titre</th>
                  <th className="text-left p-4 font-semibold text-gray-600">Localisation</th>
                  <th className="text-left p-4 font-semibold text-gray-600">Urgence</th>
                  <th className="text-left p-4 font-semibold text-gray-600">Statut</th>
                  <th className="text-left p-4 font-semibold text-gray-600">Déclarant</th>
                  <th className="text-right p-4 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSignalements.map((sig) => (
                  <tr key={sig.id} className="hover:bg-gray-50">
                    <td className="p-4 text-gray-600 text-sm">{formatDate(sig.dateSignalement)}</td>
                    <td className="p-4">
                      <Link
                        to={`/admin/signalements/${sig.id}`}
                        className="font-medium text-gray-800 hover:text-blue-600"
                      >
                        {sig.titre}
                      </Link>
                      <p className="text-sm text-gray-500 truncate max-w-xs">{sig.description}</p>
                    </td>
                    <td className="p-4 text-gray-600 text-sm">{sig.localisationDetail}</td>
                    <td className="p-4">
                      <span className={`font-medium ${URGENCES.find((u) => u.value === sig.urgence)?.color}`}>
                        {URGENCES.find((u) => u.value === sig.urgence)?.label}
                      </span>
                    </td>
                    <td className="p-4">
                      <select
                        value={sig.statut}
                        onChange={(e) => changerStatut(sig.id, e.target.value as SignalementStatut)}
                        disabled={updating === sig.id}
                        className={`px-3 py-1 rounded-full text-sm font-medium border-0 cursor-pointer
                          ${STATUTS.find((s) => s.value === sig.statut)?.color}
                          ${updating === sig.id ? 'opacity-50' : ''}`}
                      >
                        {STATUTS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4 text-gray-600 text-sm">{sig.declarant.nom}</td>
                    <td className="p-4 text-right">
                      <Link
                        to={`/admin/signalements/${sig.id}`}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Voir
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Version mobile : cartes */}
          <div className="lg:hidden divide-y divide-gray-100">
            {filteredSignalements.map((sig) => (
              <div key={sig.id} className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <Link
                    to={`/admin/signalements/${sig.id}`}
                    className="font-medium text-gray-800 hover:text-blue-600"
                  >
                    {sig.titre}
                  </Link>
                  <span className={`text-lg ${URGENCES.find((u) => u.value === sig.urgence)?.color}`}>•</span>
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  {sig.localisationDetail} • {formatDate(sig.dateSignalement)}
                </p>
                <div className="flex items-center justify-between">
                  <select
                    value={sig.statut}
                    onChange={(e) => changerStatut(sig.id, e.target.value as SignalementStatut)}
                    disabled={updating === sig.id}
                    className={`px-3 py-1 rounded-full text-sm font-medium border-0
                      ${STATUTS.find((s) => s.value === sig.statut)?.color}
                      ${updating === sig.id ? 'opacity-50' : ''}`}
                  >
                    {STATUTS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  <Link
                    to={`/admin/signalements/${sig.id}`}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Détails
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default SignalementsPage;
